"use client";

import { useState, useCallback, useMemo } from "react";
import type { PaymentPageItem } from "@/lib/api/payment-page.schema";

interface CartEntry {
  item: PaymentPageItem;
  quantity: number;
}

export function useCart() {
  const [entries, setEntries] = useState<Map<string, CartEntry>>(new Map());

  const addItem = useCallback((item: PaymentPageItem) => {
    setEntries((prev) => {
      const next = new Map(prev);
      const existing = next.get(item.id);
      const currentQty = existing?.quantity ?? 0;

      // A cart is paid as one charge, so every item must share a currency
      const cartCurrency = prev.values().next().value?.item.currency;
      if (cartCurrency && cartCurrency !== item.currency) return prev;

      // Only enforce stock limit when tracking quantity
      if (item.track_quantity && currentQty >= item.quantity) return prev;

      next.set(item.id, { item, quantity: currentQty + 1 });
      return next;
    });
  }, []);

  const removeItem = useCallback((stockId: string) => {
    setEntries((prev) => {
      const next = new Map(prev);
      next.delete(stockId);
      return next;
    });
  }, []);

  const updateQuantity = useCallback(
    (stockId: string, quantity: number) => {
      setEntries((prev) => {
        const next = new Map(prev);
        const existing = next.get(stockId);
        if (!existing) return prev;

        if (quantity <= 0) {
          next.delete(stockId);
          return next;
        }

        const clampedQty = existing.item.track_quantity
          ? Math.min(quantity, existing.item.quantity)
          : quantity;
        next.set(stockId, { ...existing, quantity: clampedQty });
        return next;
      });
    },
    [],
  );

  const clearCart = useCallback(() => {
    setEntries(new Map());
  }, []);

  const cartItems = useMemo(() => Array.from(entries.values()), [entries]);

  const totalAmount = useMemo(
    () => cartItems.reduce((sum, e) => sum + e.item.price * e.quantity, 0),
    [cartItems],
  );

  const totalItems = useMemo(
    () => cartItems.reduce((sum, e) => sum + e.quantity, 0),
    [cartItems],
  );

  /** Currency of everything in the cart (items never mix); null when empty */
  const currency = cartItems[0]?.item.currency ?? null;

  /** Whether an item can join the cart alongside what is already in it */
  const acceptsItem = useCallback(
    (item: PaymentPageItem) => currency === null || item.currency === currency,
    [currency],
  );

  const getItemQuantity = useCallback(
    (stockId: string) => entries.get(stockId)?.quantity ?? 0,
    [entries],
  );

  return {
    cartItems,
    totalAmount,
    totalItems,
    currency,
    acceptsItem,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getItemQuantity,
  };
}

export type Cart = ReturnType<typeof useCart>;
